import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { bmpData } from '@/lib/bmpData';
import SEO from '@/components/seo/SEO';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, BookOpen, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';

const BMPIndexPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredBMPs = bmpData.filter(bmp => 
    bmp.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    bmp.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bmp.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-shell">
      <SEO 
        title="SWPPP BMP Field Guide - Inspection Requirements"
        description="The complete encyclopedia of Stormwater Best Management Practices (BMPs). Learn what to look for, common failures, and maintenance tips for Silt Fences, Inlets, and more."
        canonical="/resources/bmp-field-guide"
      />

      <div className="page-intro">
        <div className="container relative z-10 mx-auto max-w-4xl px-4 text-center">
           <Breadcrumbs items={[
               { label: 'Resources', href: '/' },
               { label: 'BMP Field Guide', href: '/resources/bmp-field-guide' }
           ]} />

           <div className="eyebrow mb-6">
             <BookOpen size={15} /> Inspection reference
           </div>
           <h1 className="mb-6 text-4xl font-black tracking-[-0.04em] md:text-6xl">
             The SWPPP Field Guide
           </h1>
           <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
             Your pocket reference for erosion and sediment control. Learn exactly what inspectors look for.
           </p>
           
           <div className="relative max-w-md mx-auto">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
             <Input aria-label="Search best management practices"
                placeholder="Search BMPs (e.g. Silt Fence)..." 
                className="h-12 rounded-xl bg-background pl-10 text-base shadow-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
             />
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBMPs.length > 0 ? filteredBMPs.map((bmp) => (
                <Link key={bmp.slug} to={`/resources/bmp-field-guide/${bmp.slug}`} className="group h-full">
                    <Card className="h-full transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg">
                        <CardHeader>
                            <div className="flex justify-between items-start mb-2">
                                <Badge variant="outline" className="text-xs font-normal">{bmp.category}</Badge>
                            </div>
                            <CardTitle className="transition-colors group-hover:text-primary">{bmp.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                                {bmp.description}
                            </p>
                            <div className="mt-auto flex items-center text-sm font-semibold text-primary">
                                View Inspection Guide <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform"/>
                            </div>
                        </CardContent>
                    </Card>
                </Link>
            )) : (
                <div className="col-span-full text-center py-20">
                    <p className="text-muted-foreground">No BMPs found matching your search.</p>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default BMPIndexPage;
